/**
 * 各マスタ画面から履歴画面へ入るときの「対象」を作る (マスタ変更履歴 SBI 7)。
 *
 * `entity_key` は履歴テーブルの PK で、`<master_type>#<主キーの値...>` 形式。
 * バックエンド `lambda/control-panel-api/index.py` の `_record_*_history` が書き込む値と
 * 1 文字違っても別のデータ扱いになる (履歴が 0 件に見える) ため、組み立てはここだけで行う。
 * 各画面は主キーの値を渡すだけにし、`#` で連結する形式を知らなくてよくする。
 *
 * 主キーの値はエスケープしない (バックエンドも生の値を連結する)。
 */
import type { MasterHistoryType } from '../api/types';

import { buildEntityPath } from './masterHistoryView';

/** 履歴の対象データ 1 件 (種別と PK) */
export interface MasterHistoryTarget {
  masterType: MasterHistoryType;
  entityKey: string;
}

function target(masterType: MasterHistoryType, ...keyParts: string[]): MasterHistoryTarget {
  return { masterType, entityKey: [masterType, ...keyParts].join('#') };
}

/** 放送地点。`routeDirection` 自体が `線区#方面` で `#` を含む */
export function broadcastSpotTarget(routeDirection: string, spotId: string): MasterHistoryTarget {
  return target('broadcast_spot', routeDirection, spotId);
}

export function chimeTarget(chimeId: string): MasterHistoryTarget {
  return target('chime', chimeId);
}

/** 定型文 (種別 x 言語) */
export function phraseTemplateTarget(templateId: string, languageKey: string): MasterHistoryTarget {
  return target('phrase_template', templateId, languageKey);
}

export function ssmlDictionaryTarget(languageKey: string, sourceText: string): MasterHistoryTarget {
  return target('ssml_dictionary', languageKey, sourceText);
}

/** 任意テキスト。全線区共通は `routeId` に `__all__` をそのまま渡す */
export function freeTextTemplateTarget(routeId: string, templateId: string): MasterHistoryTarget {
  return target('free_text_template', routeId, templateId);
}

export function permissionGroupTarget(groupId: string): MasterHistoryTarget {
  return target('permission_group', groupId);
}

/** 端末。主キーは編成番号 (`15#014` のように `#` を含む) */
export function deviceTarget(vehicleId: string): MasterHistoryTarget {
  return target('device', vehicleId);
}

/** ② 対象データの履歴一覧の URL (変更通知メールのリンクと同じ `master_type` + `entity_key`) */
export function buildTargetPath(historyTarget: MasterHistoryTarget): string {
  return buildEntityPath(historyTarget.entityKey, historyTarget.masterType);
}
